/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { SoggettoGiuridicoRichiestaDTO } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce il soggetto giuridico con chiave id
 * @summary Restituisce il soggetto giuridico
 */
export const getFrontSoggettiGiuridiciRichiestaId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<SoggettoGiuridicoRichiestaDTO>(
    {
      url: `/framework-cmp-domande/soggetti-giuridici-richiesta/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontSoggettiGiuridiciRichiestaIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/soggetti-giuridici-richiesta/${id}`] as const;

export const getGetFrontSoggettiGiuridiciRichiestaIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontSoggettiGiuridiciRichiestaIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>
  > = ({ signal }) =>
    getFrontSoggettiGiuridiciRichiestaId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontSoggettiGiuridiciRichiestaIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>
>;
export type GetFrontSoggettiGiuridiciRichiestaIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce il soggetto giuridico
 */
export const useGetFrontSoggettiGiuridiciRichiestaId = <
  TData = Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontSoggettiGiuridiciRichiestaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontSoggettiGiuridiciRichiestaIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un soggetto giuridico
 * @summary Aggiorna soggetto giuridico
 */
export const putFrontSoggettiGiuridiciRichiestaId = (
  id: string,
  soggettoGiuridicoRichiestaDTO: SoggettoGiuridicoRichiestaDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SoggettoGiuridicoRichiestaDTO>(
    {
      url: `/framework-cmp-domande/soggetti-giuridici-richiesta/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: soggettoGiuridicoRichiestaDTO,
    },
    options,
  );
};

export const getPutFrontSoggettiGiuridiciRichiestaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontSoggettiGiuridiciRichiestaId>>,
    TError,
    { id: string; data: SoggettoGiuridicoRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontSoggettiGiuridiciRichiestaId>>,
  TError,
  { id: string; data: SoggettoGiuridicoRichiestaDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontSoggettiGiuridiciRichiestaId>>,
    { id: string; data: SoggettoGiuridicoRichiestaDTO }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontSoggettiGiuridiciRichiestaId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontSoggettiGiuridiciRichiestaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontSoggettiGiuridiciRichiestaId>>
>;
export type PutFrontSoggettiGiuridiciRichiestaIdMutationBody =
  SoggettoGiuridicoRichiestaDTO;
export type PutFrontSoggettiGiuridiciRichiestaIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiorna soggetto giuridico
 */
export const usePutFrontSoggettiGiuridiciRichiestaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontSoggettiGiuridiciRichiestaId>>,
    TError,
    { id: string; data: SoggettoGiuridicoRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontSoggettiGiuridiciRichiestaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiunge un nuovo soggetto giuridico
 * @summary Aggiunta nuovo soggetto giuridico
 */
export const postFrontSoggettiGiuridiciRichiesta = (
  soggettoGiuridicoRichiestaDTO: SoggettoGiuridicoRichiestaDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SoggettoGiuridicoRichiestaDTO>(
    {
      url: `/framework-cmp-domande/soggetti-giuridici-richiesta`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: soggettoGiuridicoRichiestaDTO,
    },
    options,
  );
};

export const getPostFrontSoggettiGiuridiciRichiestaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontSoggettiGiuridiciRichiesta>>,
    TError,
    { data: SoggettoGiuridicoRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontSoggettiGiuridiciRichiesta>>,
  TError,
  { data: SoggettoGiuridicoRichiestaDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontSoggettiGiuridiciRichiesta>>,
    { data: SoggettoGiuridicoRichiestaDTO }
  > = props => {
    const { data } = props ?? {};

    return postFrontSoggettiGiuridiciRichiesta(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontSoggettiGiuridiciRichiestaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontSoggettiGiuridiciRichiesta>>
>;
export type PostFrontSoggettiGiuridiciRichiestaMutationBody =
  SoggettoGiuridicoRichiestaDTO;
export type PostFrontSoggettiGiuridiciRichiestaMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiunta nuovo soggetto giuridico
 */
export const usePostFrontSoggettiGiuridiciRichiesta = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontSoggettiGiuridiciRichiesta>>,
    TError,
    { data: SoggettoGiuridicoRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontSoggettiGiuridiciRichiestaMutationOptions(options);

  return useMutation(mutationOptions);
};
